//
// Weight Management phase header: which of Assessment, Selection and Supply each weight pathway belongs to, and the
// step totals for the two phases that have no refinedFlowUi. Totals are main-route estimates because these flows
// branch heavily; the header never shows a total below the current step.
//

import type { Pathway } from "../../../types";
import { WEIGHT_ASSESSMENT_STEP_TOTAL } from "./weightFlowSteps";

type FlowPhase = NonNullable<Pathway["flowPhase"]>;

// The selector and shared assessment get their step numbers from refinedFlowUi.
const ASSESSMENT_PATHWAYS: Record<string, FlowPhase> = {
  "aht-wt-selector": { phase: "assessment" },
  "aht-wt-shared-assessment": { phase: "assessment" },
  // Re-initiation picks its drug after the consultation type question (step 1).
  "aht-wt-reinitiation-drug-select": { phase: "assessment", stepOffset: 1, stepTotal: WEIGHT_ASSESSMENT_STEP_TOTAL },
};

export const WEIGHT_SELECTION_PATHWAY_ID = "aht-weight-selection";
export const WEIGHT_SELECTION_STEP_TOTAL = 17;

// Screens on the clear route to a supply outcome (interactive screens only).
export const WEIGHT_SUPPLY_STEP_TOTALS: Record<string, number> = {
  "aht-wt-tirzepatide-initial": 5,
  "aht-wt-semaglutide-initial": 5,
  "aht-wt-oral-semaglutide-initial": 9,
  "aht-wt-foundayo-initial": 6,
  "aht-wt-foundayo-transfer": 9,
  "aht-wt-foundayo-reinitiation": 11,
  "aht-wt-liraglutide-initial": 6,
  "aht-wt-mysimba-initial": 5,
  "aht-wt-orlistat-initial": 5,
  "aht-wt-tirzepatide-rescreen": 13,
  "aht-wt-semaglutide-rescreen": 13,
  "aht-wt-liraglutide-rescreen": 14,
  "aht-wt-tirzepatide-transfer": 16,
  "aht-wt-semaglutide-transfer": 17,
  "aht-wt-liraglutide-transfer": 16,
  "aht-wt-mysimba-transfer": 10,
  "aht-wt-orlistat-transfer": 9,
};

export function weightFlowPhase(pathwayId: string): FlowPhase | undefined {
  const assessment = ASSESSMENT_PATHWAYS[pathwayId];
  if (assessment) return assessment;
  if (pathwayId === WEIGHT_SELECTION_PATHWAY_ID) return { phase: "selection", stepTotal: WEIGHT_SELECTION_STEP_TOTAL };
  const supplyTotal = WEIGHT_SUPPLY_STEP_TOTALS[pathwayId];
  return supplyTotal === undefined ? undefined : { phase: "supply", stepTotal: supplyTotal };
}

/** Returns the registry with the phase header switched on for every weight pathway; other pathways are untouched. */
export function withWeightFlowPhases(registry: Record<string, Pathway>): Record<string, Pathway> {
  return Object.fromEntries(
    Object.entries(registry).map(([id, pathway]) => {
      const flowPhase = weightFlowPhase(id);
      return [id, flowPhase ? { ...pathway, flowPhase } : pathway];
    }),
  );
}
